import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { CalendarMonth, KeyboardArrowDown, Repeat } from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import { RECURRENCE_FREQUENCIES, WEEKDAYS } from '../../shared/appConstants';
import {
  calculateNextRecurrence,
  formatOrdinal,
  formatRecurrenceDate,
  getRecurrenceSummary,
  recurrenceConfigsEqual
} from '../../shared/recurrenceUtils';

const selectSx = {
  height: 44,
  bgcolor: COLORS.white,
  borderRadius: '8px',
  fontSize: '14px',
  color: COLORS.gray[800],
  '& .MuiSelect-select': { py: 1.25, px: 1.75 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[500] }
};

const recurrenceMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      maxHeight: 280,
      borderRadius: '8px',
      '& .MuiMenuItem-root': { minHeight: 40, px: 1.75, fontSize: '14px' }
    }
  }
};

const RecurrencePanel = ({
  isRecurring,
  recurrenceConfig,
  nextRepOn,
  initialRecurrenceConfig,
  onChange,
  readOnly = false,
  compact = false
}) => {
  const { t } = useTranslation();
  const [monthlyCalendarAnchor, setMonthlyCalendarAnchor] = useState(null);

  const frequencyLabels = {
    [RECURRENCE_FREQUENCIES.WEEKLY]: t('taskModal.recurrence.frequencies.weekly'),
    [RECURRENCE_FREQUENCIES.MONTHLY]: t('taskModal.recurrence.frequencies.monthly'),
    [RECURRENCE_FREQUENCIES.YEARLY]: t('taskModal.recurrence.frequencies.yearly')
  };

  if (readOnly && !isRecurring) return null;

  const updateConfig = (patch) => {
    const nextConfig = { ...(recurrenceConfig || {}), ...patch };
    if (patch.frequency) delete nextConfig.repeats_on;
    onChange?.({ isRecurring: true, recurrenceConfig: nextConfig });
  };

  const summary = getRecurrenceSummary(recurrenceConfig, t, readOnly);

  if (readOnly) {
    return (
      <Box sx={{ border: `1px solid ${COLORS.ui.ghostBorder}`, bgcolor: COLORS.ui.ghostBg, borderRadius: '8px', px: 1.5, py: 1.25, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Repeat sx={{ fontSize: 16, color: COLORS.primary }} />
          <Typography sx={{ fontSize: compact ? '12px' : '13px', fontWeight: 500 }}>
            {t('taskModal.recurrence.title')}
          </Typography>
        </Box>
        <Typography sx={{ mt: 0.75, fontSize: compact ? '11px' : '12px', color: COLORS.gray[700], lineHeight: 1.5 }}>
          {t('taskModal.recurrence.nextOccurrence', { summary, date: formatRecurrenceDate(nextRepOn) })}
        </Typography>
      </Box>
    );
  }

  const configWasChanged = initialRecurrenceConfig !== undefined
    && !recurrenceConfigsEqual(recurrenceConfig, initialRecurrenceConfig);
  const displayedNext = configWasChanged || !nextRepOn
    ? calculateNextRecurrence(recurrenceConfig)
    : nextRepOn;

  return (
    <Box sx={{ border: `1px solid ${COLORS.ui.ghostBorder}`, bgcolor: COLORS.ui.ghostBg, borderRadius: '8px', px: 2, py: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Repeat sx={{ fontSize: 17 }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
            {t('taskModal.recurrence.title')}
          </Typography>
        </Box>
        <Switch
          checked={Boolean(isRecurring)}
          onChange={(event) => onChange?.({
            isRecurring: event.target.checked,
            recurrenceConfig: event.target.checked ? (recurrenceConfig || null) : null
          })}
          slotProps={{ input: { 'aria-label': t('taskModal.recurrence.title') } }}
          sx={{
            width: 42,
            height: 24,
            p: 0,
            '& .MuiSwitch-switchBase': {
              p: '2px',
              '&.Mui-checked': {
                transform: 'translateX(18px)',
                color: COLORS.white,
                '& + .MuiSwitch-track': { bgcolor: COLORS.blue[600], opacity: 1 }
              }
            },
            '& .MuiSwitch-thumb': { width: 20, height: 20, boxShadow: 'none' },
            '& .MuiSwitch-track': { borderRadius: 12, bgcolor: COLORS.gray[400], opacity: 1 }
          }}
        />
      </Box>

      {isRecurring && (
        <>
          <Typography sx={{ mt: 1.5, mb: 0.75, fontSize: '12px', fontWeight: 500, color: COLORS.black }}>
            {t('taskModal.recurrence.frequency')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormControl size="small" fullWidth>
              <Select
                displayEmpty
                value={recurrenceConfig?.frequency || ''}
                onChange={(event) => updateConfig({ frequency: event.target.value })}
                renderValue={(value) => value ? frequencyLabels[value] : t('taskModal.recurrence.selectFrequency')}
                sx={selectSx}
                MenuProps={recurrenceMenuProps}
              >
                <MenuItem disabled value="">{t('taskModal.recurrence.selectFrequency')}</MenuItem>
                {Object.entries(frequencyLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {!recurrenceConfig?.frequency && (
              <FormControl size="small" fullWidth disabled>
                <Select
                  displayEmpty
                  value=""
                  renderValue={() => t('taskModal.recurrence.selectRepeatOption')}
                  sx={{
                    ...selectSx,
                    '&.Mui-disabled': { bgcolor: COLORS.gray[50] },
                    '& .MuiSelect-select.Mui-disabled': {
                      WebkitTextFillColor: COLORS.gray[500],
                      color: COLORS.gray[500]
                    }
                  }}
                >
                  <MenuItem value="">{t('taskModal.recurrence.selectRepeatOption')}</MenuItem>
                </Select>
              </FormControl>
            )}

            {recurrenceConfig?.frequency === RECURRENCE_FREQUENCIES.WEEKLY && (
              <FormControl size="small" fullWidth>
                <Select
                  displayEmpty
                  value={recurrenceConfig?.repeats_on || ''}
                  onChange={(event) => updateConfig({ repeats_on: event.target.value })}
                  renderValue={(value) => value
                    ? t(`taskModal.recurrence.weekdays.${value.toLowerCase()}`)
                    : t('taskModal.recurrence.selectDay')}
                  sx={selectSx}
                  MenuProps={recurrenceMenuProps}
                >
                  <MenuItem disabled value="">{t('taskModal.recurrence.selectDay')}</MenuItem>
                  {WEEKDAYS.map((day) => (
                    <MenuItem key={day} value={day}>
                      {t(`taskModal.recurrence.weekdays.${day.toLowerCase()}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {recurrenceConfig?.frequency === RECURRENCE_FREQUENCIES.MONTHLY && (
              <Box>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={(event) => setMonthlyCalendarAnchor(event.currentTarget)}
                  aria-haspopup="dialog"
                  aria-expanded={Boolean(monthlyCalendarAnchor)}
                  sx={{
                    height: 44,
                    bgcolor: COLORS.white,
                    borderColor: COLORS.gray[300],
                    borderRadius: '8px',
                    color: COLORS.gray[800],
                    textTransform: 'none',
                    justifyContent: 'space-between',
                    px: 1.75,
                    fontSize: '14px',
                    '&:hover': { borderColor: COLORS.gray[500], bgcolor: COLORS.white }
                  }}
                >
                  <span>
                    {recurrenceConfig?.repeats_on
                      ? formatOrdinal(recurrenceConfig.repeats_on)
                      : t('taskModal.recurrence.selectDate')}
                  </span>
                  <KeyboardArrowDown aria-hidden="true" sx={{ fontSize: 20, color: COLORS.gray[600] }} />
                </Button>
                <Popover
                  open={Boolean(monthlyCalendarAnchor)}
                  anchorEl={monthlyCalendarAnchor}
                  onClose={() => setMonthlyCalendarAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 0.75,
                        p: 1.5,
                        width: monthlyCalendarAnchor?.getBoundingClientRect().width || 292,
                        boxSizing: 'border-box',
                        border: `1px solid ${COLORS.primary}`,
                        borderRadius: 0,
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)'
                      }
                    }
                  }}
                >
                  <Box
                    role="grid"
                    aria-label={t('taskModal.recurrence.selectMonthlyRepeatDate')}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      columnGap: 0.5,
                      rowGap: 1
                    }}
                  >
                    {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => {
                      const selected = Number(recurrenceConfig?.repeats_on) === day;
                      return (
                        <Button
                          key={day}
                          role="gridcell"
                          aria-selected={selected}
                          onClick={() => {
                            updateConfig({ repeats_on: day });
                            setMonthlyCalendarAnchor(null);
                          }}
                          sx={{
                            minWidth: 0,
                            width: 38,
                            height: 40,
                            p: 0,
                            justifySelf: 'center',
                            borderRadius: '7px',
                            color: selected ? COLORS.primary : COLORS.gray[700],
                            bgcolor: selected ? COLORS.blue[50] : 'transparent',
                            border: selected ? `1px solid ${COLORS.primary}` : '1px solid transparent',
                            fontWeight: selected ? 600 : 400,
                            fontSize: '14px',
                            lineHeight: 1,
                            '&:hover': { bgcolor: COLORS.blue[50] }
                          }}
                        >
                          {day}
                        </Button>
                      );
                    })}
                  </Box>
                </Popover>
              </Box>
            )}

            {recurrenceConfig?.frequency === RECURRENCE_FREQUENCIES.YEARLY && (
              <TextField
                type="date"
                size="small"
                fullWidth
                value={recurrenceConfig?.repeats_on || ''}
                onChange={(event) => updateConfig({ repeats_on: event.target.value })}
                onKeyDown={(event) => event.preventDefault()}
                onPaste={(event) => event.preventDefault()}
                onDrop={(event) => event.preventDefault()}
                inputProps={{
                  min: new Date().toISOString().split('T')[0],
                  'aria-label': t('taskModal.recurrence.repeatDate'),
                  style: {
                    height: 44,
                    padding: '0 14px',
                    boxSizing: 'border-box',
                    fontSize: '14px'
                  }
                }}
                sx={{
                  width: '100%',
                  bgcolor: COLORS.white,
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-root': {
                    width: '100%',
                    height: 44,
                    p: 0,
                    fontSize: '14px'
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[500] }
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1.5 }}>
            <CalendarMonth sx={{ fontSize: 18, color: COLORS.gray[600], mt: '1px' }} />
            <Typography sx={{ fontSize: '12px', color: COLORS.gray[700], lineHeight: 1.5 }}>
              {t('taskModal.recurrence.nextOccurrence', {
                summary,
                date: formatRecurrenceDate(displayedNext)
              })}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default RecurrencePanel;
